import React, { useState } from "react";
import { Link, NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "sonner";
import {
  PortalAuthProvider,
  usePortalAuth,
} from "../../lib/portal-auth-context";
import { LOGO_URL } from "../../lib/i18n";
import {
  House,
  FilePlus,
  ChatCircle,
  Receipt,
  Truck,
  CurrencyDollar,
  Package,
  UserCircle,
  SignOut,
  List as ListIcon,
  X,
  ArrowUpRight,
} from "@phosphor-icons/react";

const NAV = [
  { to: "/portal", end: true, label: "Dashboard", icon: House },
  { to: "/portal/new-quote", label: "Nueva solicitud", icon: FilePlus },
  { to: "/portal/quotes", label: "Mis cotizaciones", icon: ChatCircle },
  { to: "/portal/orders", label: "Mis pedidos", icon: Receipt },
  { to: "/portal/shipments", label: "Mis envíos", icon: Truck },
  { to: "/portal/invoices", label: "Mis facturas", icon: CurrencyDollar },
  { to: "/portal/catalog", label: "Catálogo", icon: Package },
  { to: "/portal/profile", label: "Perfil", icon: UserCircle },
];

const TopBar = ({ user, onMenu }) => {
  const { logout } = usePortalAuth();
  const nav = useNavigate();
  return (
    <header
      className="h-20 bg-[var(--icor-navy)] text-white flex items-center justify-between px-6 lg:px-10 border-b border-white/10"
      data-testid="portal-topbar"
    >
      <div className="flex items-center gap-4">
        <button
          onClick={onMenu}
          className="lg:hidden text-white/70"
          aria-label="Menu"
          data-testid="portal-menu-btn"
        >
          <ListIcon size={22} />
        </button>
        <Link to="/portal" className="flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="ICOR"
            className="h-12 w-auto"
            style={{
              mixBlendMode: "screen",
              filter: "brightness(2.6) contrast(1.1)",
            }}
          />
          <div className="hidden sm:block leading-tight">
            <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)]">
              Portal Cliente
            </div>
            <div className="font-display text-sm tracking-tight">
              {user.company}
            </div>
          </div>
        </Link>
      </div>
      <div className="flex items-center gap-4">
        <div className="hidden md:block text-right leading-tight">
          <div className="text-sm font-medium tracking-tight">
            {user.full_name}
          </div>
          <div className="text-[10px] tracking-[0.2em] uppercase text-white/40 mt-0.5">
            {user.email}
          </div>
        </div>
        <a
          href="/"
          className="hidden md:inline-flex items-center gap-1 text-xs uppercase tracking-[0.18em] text-white/60 hover:text-white"
        >
          Ir al sitio <ArrowUpRight size={14} />
        </a>
        <button
          onClick={async () => {
            await logout();
            nav("/portal/login", { replace: true });
          }}
          className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.18em] text-white/70 hover:text-white hover:bg-white/5 rounded-sm"
          data-testid="portal-logout"
        >
          <SignOut size={14} />
          Cerrar sesión
        </button>
      </div>
    </header>
  );
};

const Sidebar = ({ onClose }) => (
  <aside
    className="w-64 bg-white border-r border-slate-200 flex flex-col h-full"
    data-testid="portal-sidebar"
  >
    <div className="px-5 h-14 flex items-center justify-between border-b border-slate-200 lg:hidden">
      <span className="font-display text-sm tracking-tight text-[var(--icor-navy)]">
        Menú
      </span>
      {onClose && (
        <button onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      )}
    </div>
    <nav className="flex-1 py-5 space-y-1 px-3 overflow-y-auto">
      {NAV.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          end={n.end}
          onClick={onClose}
          data-testid={`portal-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-all ${
              isActive
                ? "bg-[var(--icor-blue)] text-white"
                : "text-slate-600 hover:text-[var(--icor-navy)] hover:bg-slate-50"
            }`
          }
        >
          <n.icon size={18} weight="duotone" />
          {n.label}
        </NavLink>
      ))}
    </nav>
    <div className="px-5 py-4 border-t border-slate-200 text-[10px] tracking-[0.2em] uppercase text-slate-400">
      ICOR · {new Date().getFullYear()}
    </div>
  </aside>
);

const Shell = () => {
  const { user } = usePortalAuth();
  const [open, setOpen] = useState(false);

  if (user === undefined) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-slate-400 text-sm"
        data-testid="portal-loading"
      >
        Cargando…
      </div>
    );
  }
  if (!user) return <Navigate to="/portal/login" replace />;

  return (
    <div
      className="min-h-screen flex flex-col bg-slate-50"
      data-testid="portal-shell"
    >
      <TopBar user={user} onMenu={() => setOpen(true)} />
      <div className="flex flex-1 min-h-0">
        <div className="hidden lg:block sticky top-0 h-[calc(100vh-5rem)]">
          <Sidebar />
        </div>
        {open && (
          <div className="lg:hidden fixed inset-0 z-50 flex">
            <div
              className="absolute inset-0 bg-black/60"
              onClick={() => setOpen(false)}
            />
            <div className="relative h-full">
              <Sidebar onClose={() => setOpen(false)} />
            </div>
          </div>
        )}
        <main className="flex-1 p-6 lg:p-10 overflow-x-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default function PortalLayout() {
  return (
    <PortalAuthProvider>
      <Shell />
      <Toaster position="top-right" richColors />
    </PortalAuthProvider>
  );
}
